/**
 * Sentry Quota Management Utility
 * 
 * Manages Sentry free tier quota limits with localStorage tracking:
 * - 5,000 errors/month
 * - 10,000 performance transactions/month
 * - 50 session replays/month
 * 
 * Resets on the 1st of each calendar month.
 * Supports priority-based filtering where 'critical' events bypass limits.
 */

interface QuotaLimits {
  errors: number;
  transactions: number;
  replays: number;
}

interface QuotaCounts {
  errors: number;
  transactions: number;
  replays: number;
  month: string; // Format: YYYY-MM
}

const QUOTA_LIMITS: QuotaLimits = {
  errors: 5000,
  transactions: 10000,
  replays: 50,
};

const QUOTA_STORAGE_KEY = 'sentry_quota_counts';
const WARNING_THRESHOLD = 0.9; // Warn at 90%

/**
 * Get the current month in YYYY-MM format
 */
function getCurrentMonth(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Load quota counts from localStorage, resetting if new month
 */
function loadQuotaCounts(): QuotaCounts {
  const currentMonth = getCurrentMonth();
  
  try {
    const stored = localStorage.getItem(QUOTA_STORAGE_KEY);
    if (stored) {
      const counts: QuotaCounts = JSON.parse(stored);
      
      // Reset if we're in a new month
      if (counts.month !== currentMonth) {
        return {
          errors: 0,
          transactions: 0,
          replays: 0,
          month: currentMonth,
        };
      }
      
      return counts;
    }
  } catch (error) {
    console.warn('Failed to load Sentry quota counts:', error);
  }
  
  // Default counts
  return {
    errors: 0,
    transactions: 0,
    replays: 0,
    month: currentMonth,
  };
}

/**
 * Save quota counts to localStorage
 */
function saveQuotaCounts(counts: QuotaCounts): void {
  try {
    localStorage.setItem(QUOTA_STORAGE_KEY, JSON.stringify(counts));
  } catch (error) {
    console.warn('Failed to save Sentry quota counts:', error);
  }
}

/**
 * Increment a quota counter
 */
function incrementQuota(type: keyof QuotaLimits): void {
  const counts = loadQuotaCounts();
  counts[type]++;
  saveQuotaCounts(counts);
  
  // Check if we're approaching the limit
  const limit = QUOTA_LIMITS[type];
  const percentage = counts[type] / limit;
  
  if (percentage >= WARNING_THRESHOLD && percentage < 1.0) {
    console.warn(
      `Sentry ${type} quota at ${Math.round(percentage * 100)}% (${counts[type]}/${limit})`
    );
  } else if (percentage >= 1.0) {
    console.warn(
      `Sentry ${type} quota exceeded (${counts[type]}/${limit}). Events may be dropped.`
    );
  }
}

/**
 * Check if we're within quota for a given type
 */
function isWithinQuota(type: keyof QuotaLimits): boolean {
  const counts = loadQuotaCounts();
  return counts[type] < QUOTA_LIMITS[type];
}

/**
 * Get current quota usage
 */
export function getQuotaUsage(): {
  errors: { used: number; limit: number; percentage: number };
  transactions: { used: number; limit: number; percentage: number };
  replays: { used: number; limit: number; percentage: number };
  month: string;
} {
  const counts = loadQuotaCounts();
  
  return {
    errors: {
      used: counts.errors,
      limit: QUOTA_LIMITS.errors,
      percentage: (counts.errors / QUOTA_LIMITS.errors) * 100,
    },
    transactions: {
      used: counts.transactions,
      limit: QUOTA_LIMITS.transactions,
      percentage: (counts.transactions / QUOTA_LIMITS.transactions) * 100,
    },
    replays: {
      used: counts.replays,
      limit: QUOTA_LIMITS.replays,
      percentage: (counts.replays / QUOTA_LIMITS.replays) * 100,
    },
    month: counts.month,
  };
}

/**
 * Manually reset quota counts (for testing)
 */
export function resetQuotaCounts(): void {
  const currentMonth = getCurrentMonth();
  saveQuotaCounts({
    errors: 0,
    transactions: 0,
    replays: 0,
    month: currentMonth,
  });
}

/**
 * beforeSend hook for Sentry error events
 * Returns null to drop event, or event to send it
 */
export function beforeSendWithQuota(
  event: any,
  hint?: any
): any | null {
  // Filter out AUTH session check errors (common noise, not critical)
  const message = event.message || '';
  const loggerEntry = event.logger || '';
  
  // Drop AUTH session check failures - these are expected during normal operation
  if (
    message.includes('[AUTH:SESSION_CHECK_FAILED]') ||
    message.includes('[AUTH:SESSION_RETRY_FAILED]') ||
    message.includes('[AUTH:EXISTING_SESSION_INVALID]') ||
    loggerEntry.includes('session_check_failed') ||
    loggerEntry.includes('session_retry_failed')
  ) {
    return null; // Drop without counting against quota
  }
  
  // Check if this is a critical priority error (always send)
  const priority = event.tags?.priority;
  if (priority === 'critical') {
    incrementQuota('errors');
    return event;
  }
  
  // Check quota for normal priority errors
  if (!isWithinQuota('errors')) {
    console.warn('Sentry error quota exceeded, dropping event');
    return null;
  }
  
  incrementQuota('errors');
  return event;
}

/**
 * beforeSendTransaction hook for Sentry performance events
 * Returns null to drop event, or event to send it
 */
export function beforeSendTransactionWithQuota(
  event: any
): any | null {
  // Check if this is a critical priority transaction (always send)
  const priority = event.tags?.priority;
  if (priority === 'critical') {
    incrementQuota('transactions');
    return event;
  }
  
  // Check quota for normal priority transactions
  if (!isWithinQuota('transactions')) {
    // Drop the transaction silently (don't log to avoid console spam)
    return null;
  }
  
  incrementQuota('transactions');
  return event;
}

/**
 * Get dynamic sample rate for replays based on quota
 */
export function getReplaySampleRate(baseRate: number): number {
  const counts = loadQuotaCounts();
  const percentage = counts.replays / QUOTA_LIMITS.replays;
  
  // If we're over quota, stop recording replays
  if (percentage >= 1.0) {
    return 0;
  }
  
  // If we're at 90% or higher, reduce the sample rate
  if (percentage >= WARNING_THRESHOLD) {
    return baseRate * 0.1; // Reduce to 10% of base rate
  }
  
  return baseRate;
}

/**
 * Track replay creation
 */
export function trackReplayCreated(): void {
  incrementQuota('replays');
}
